import { useEffect, useRef, useState } from 'react';
import type { ITopWin } from '../types/topWins.types';

const TICK_MS = 4000;
const EXIT_ANIMATION_MS = 550;

export interface RotatingWin {
  win: ITopWin;
  seq: number;
}

export interface RotatingQueueState {
  visible: RotatingWin[];
  exiting: RotatingWin | null;
}

/**
 * Drives the rotating-cards animation:
 *
 * - `visible` is topped up to `capacity` on first non-empty snapshot AND on
 *   capacity growth (portrait → landscape rotate), so new slots fill
 *   immediately instead of dripping in over many ticks.
 * - Every TICK_MS the newest unseen win is promoted to position 0; the card
 *   pushed past `capacity` becomes `exiting` for one EXIT_ANIMATION_MS frame.
 * - When every unique id has been shown, the seen set resets to only the
 *   currently-visible ids so rotation loops indefinitely even when BE keeps
 *   returning the same snapshot.
 */
export const useRotatingWinsQueue = (recent: ITopWin[], capacity: number): RotatingQueueState => {
  const [queue, setQueue] = useState<RotatingWin[]>([]);
  const seedRef = useRef(0);
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Refs mirror state for the tick interval — reading them avoids restarting
  // the interval on every poll/render (which would jitter the animation).
  const recentRef = useRef(recent);
  const queueRef = useRef(queue);

  useEffect(() => {
    recentRef.current = recent;
  }, [recent]);

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  const hasRecent = recent.length > 0;

  useEffect(() => {
    if (!hasRecent) {
      setQueue([]);
      seenIdsRef.current.clear();
      seedRef.current = 0;
      return;
    }
    setQueue((prev) => {
      if (prev.length >= capacity) return prev;
      // Fill the new slots with any wins not already on screen — gated by what's
      // visible now, NOT by the historical "seen" set. So rotating to a wider
      // layout (portrait 3 → landscape 6) shows all the extra games at once
      // instead of dripping them in over ticks, even after rotation has already
      // cycled through every game (which would otherwise mark them all seen).
      const onScreen = new Set(prev.map((item) => item.win.id));
      const additions = takeForSlots(
        recent,
        capacity - prev.length,
        onScreen,
        seenIdsRef.current,
        seedRef.current,
      );
      if (additions.length === 0) return prev;
      seedRef.current += additions.length;
      return [...prev, ...additions];
    });
  }, [hasRecent, recent, capacity]);

  useEffect(() => {
    if (!hasRecent) return;
    const intervalId = window.setInterval(() => {
      const next = pickNextOrLoop(recentRef.current, queueRef.current, capacity, seenIdsRef);
      if (!next) return;
      setQueue((prev) => [{ win: next, seq: seedRef.current++ }, ...prev]);
    }, TICK_MS);
    return () => window.clearInterval(intervalId);
  }, [hasRecent, capacity]);

  useEffect(() => {
    if (queue.length <= capacity) return;
    const timer = window.setTimeout(() => {
      setQueue((prev) => (prev.length > capacity ? prev.slice(0, capacity) : prev));
    }, EXIT_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [queue.length, capacity]);

  return {
    visible: queue.slice(0, capacity),
    exiting: queue.length > capacity ? (queue[capacity] ?? null) : null,
  };
};

/** Fill up to `count` slots with wins not currently on screen, marking each
 *  seen so the tick rotation accounting stays consistent. */
const takeForSlots = (
  recent: ITopWin[],
  count: number,
  onScreen: Set<string>,
  seen: Set<string>,
  baseSeq: number,
): RotatingWin[] => {
  const out: RotatingWin[] = [];
  for (const win of recent) {
    if (out.length >= count) break;
    if (onScreen.has(win.id)) continue;
    out.push({ win, seq: baseSeq + out.length });
    seen.add(win.id);
  }
  return out;
};

/** Pick the next unseen win; if all are seen, recycle the set (excluding what
 *  is currently on screen) and try again. Returns null when even after recycle
 *  there is nothing new (e.g. recent.length ≤ capacity). */
const pickNextOrLoop = (
  recent: ITopWin[],
  queue: RotatingWin[],
  capacity: number,
  seenRef: { current: Set<string> },
): ITopWin | null => {
  const first = recent.find((w) => !seenRef.current.has(w.id));
  if (first) {
    seenRef.current.add(first.id);
    return first;
  }
  const visibleIds = new Set<string>();
  for (const item of queue.slice(0, capacity)) visibleIds.add(item.win.id);
  seenRef.current = visibleIds;
  const second = recent.find((w) => !seenRef.current.has(w.id));
  if (!second) return null;
  seenRef.current.add(second.id);
  return second;
};
